import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const ProfilerEvaluationTileWrapper = styled.div`
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const ProfilerEvaluationTileContainer = styled.div`
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 8px;
  background: white;
`;

const ProfilerEvaluationTitle = styled(Typography)`
  font-weight: 500;
  margin-bottom: 8px;
`;

const ProfilerEvaluationStatus = styled(Typography)`
  color: ${props => props.completed ? '#4caf50' : '#757575'};
  font-size: 0.875rem;
`;

const ProfilerEvaluationDate = styled(Typography)`
  color: #757575;
  font-size: 0.75rem;
  margin-top: 4px;
`;

const ProfilerEvaluationTile = ({ evaluation }) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const getEvaluationTypeLabel = (type) => {
    switch (type) {
      case 'site-finder':
        return 'Site Finder';
      case 'scraped-content':
        return 'Scraped Content';
      case 'candidate-profiler':
        return 'Candidate Profiler';
      default:
        return type;
    }
  };

  const formatAnalyticsKey = (key) => {
    // First split by underscore, then by camelCase
    return key
      .split('_')
      .map(part => 
        part
          // Insert space before capital letters
          .replace(/([A-Z])/g, ' $1')
          // Capitalize first letter
          .replace(/^./, str => str.toUpperCase())
          .trim()
      )
      .join(' ');
  };

  const renderAnalyticsValue = (value) => {
    if (typeof value === 'number') {
      return Math.round(value * 100) / 100;
    }
    if (Array.isArray(value)) {
      return (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>View Details ({value.length} items)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {value.map((item, index) => (
              <Typography key={index} variant="body2" style={{ marginBottom: '4px' }}>
                {item}
              </Typography>
            ))}
          </AccordionDetails>
        </Accordion>
      );
    }
    if (typeof value === 'object' && value !== null) {
      return null; // Skip rendering objects for now
    }
    return value;
  };

  return (
    <>
      <ProfilerEvaluationTileWrapper onClick={handleOpen}>
        <ProfilerEvaluationTileContainer>
          <ProfilerEvaluationTitle>
            {getEvaluationTypeLabel(evaluation.type)}
          </ProfilerEvaluationTitle>
          <ProfilerEvaluationStatus completed={evaluation.status === 'evaluated'}>
            {evaluation.status === 'evaluated' ? 'Completed' : 'In Progress'}
          </ProfilerEvaluationStatus>
          <ProfilerEvaluationDate>
            Last updated: {moment(evaluation.updatedAt).format('MMM D, YYYY h:mm A')}
          </ProfilerEvaluationDate>
        </ProfilerEvaluationTileContainer>
      </ProfilerEvaluationTileWrapper>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {getEvaluationTypeLabel(evaluation.type)} Evaluation
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" gutterBottom>
            Status: {evaluation.status === 'evaluated' ? 'Completed' : 'In Progress'}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            Last Updated: {moment(evaluation.updatedAt).format('MMM D, YYYY h:mm A')}
          </Typography>
          
          {evaluation.analytics && (
            <div style={{ marginTop: '16px' }}>
              <Typography variant="h6" gutterBottom>
                Analytics
              </Typography>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '200px 1fr',
                gap: '8px',
                alignItems: 'start'
              }}>
                {Object.entries(evaluation.analytics).map(([key, value]) => {
                  const renderedValue = renderAnalyticsValue(value);
                  if (renderedValue === null) return null;
                  
                  return (
                    <React.Fragment key={key}>
                      <Typography 
                        variant="subtitle2" 
                        style={{ 
                          padding: '8px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '4px'
                        }}
                      >
                        {formatAnalyticsKey(key)}
                      </Typography>
                      <div style={{ padding: '8px' }}>
                        {renderedValue}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ProfilerEvaluationTile.propTypes = {
  evaluation: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['site-finder', 'scraped-content', 'candidate-profiler']).isRequired,
    status: PropTypes.oneOf(['processed', 'evaluated']).isRequired,
    updatedAt: PropTypes.string.isRequired,
    analytics: PropTypes.object,
  }).isRequired,
};

export default ProfilerEvaluationTile; 